import type { DeliveryAddressParams } from '@iheartjane/data-access';
import type { Address } from '@iheartjane/types';
import { createExternalStore, Storage } from '@iheartjane/utils';
import { useSyncExternalStore } from 'react';

// Not the slice's `deliveryAddress` key: flag-on the slice cart is empty, so
// `clearCart()` wipes that key on boot. Cleared with the cart uuid instead.
const DELIVERY_ADDRESS_KEY = 'cart-delivery-address';

const { notify, stable, subscribe } = createExternalStore<Address | null>();

export const getLocalDeliveryAddress = (): Address | null =>
  stable(Storage.get<Address | null>(DELIVERY_ADDRESS_KEY) ?? null);

export const updateLocalDeliveryAddress = (address: Address | null) => {
  if (address) {
    Storage.set(DELIVERY_ADDRESS_KEY, address);
  } else {
    Storage.remove(DELIVERY_ADDRESS_KEY);
  }

  notify();
};

/** Hooks can't be conditional, so `enabled` skips the storage read instead. */
export const useLocalDeliveryAddress = (enabled = true) =>
  useSyncExternalStore(
    subscribe,
    () => (enabled ? getLocalDeliveryAddress() : null),
    () => null
  );

/**
 * Part of the query key, so fields the API doesn't use must not change it.
 * Sent in every mode: the API only applies it to delivery carts.
 */
export const toDeliveryAddressParams = (
  address: Address | null | undefined
): DeliveryAddressParams | undefined => {
  if (!address) return undefined;

  const params: DeliveryAddressParams = {
    ...(address.city != null && { city: address.city }),
    ...(address.country_code != null && { country_code: address.country_code }),
    ...(address.lat != null && { latitude: address.lat }),
    ...(address.lng != null && { longitude: address.lng }),
    ...(address.state_code != null && { state_code: address.state_code }),
    ...(address.street != null && { street: address.street }),
    ...(address.street2 != null && { street2: address.street2 }),
    ...(address.zipcode != null && { zipcode: address.zipcode }),
  };

  return Object.keys(params).length ? params : undefined;
};
